import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLiving } from '../../contexts/LivingContext';
import { registerParallax, isScrolling, seeded, clamp } from '../../lib/living';
import CloudShape from './CloudShape';
import './LivingWeather.css';

/* Full-screen ambient layer behind every page:
 *   1. a sky wash + sun/moon glow that follows the real sun
 *   2. slow drifting aurora blobs (compositor-only transforms)
 *   3. a canvas particle field chosen by season, time of day and weather
 * Particles pause when the tab is hidden and are skipped entirely when
 * motion is off.
 */

const PALETTES = {
    hail: ['#f4f8ff', '#e6eefa'],
    petal: ['#f9c9d4', '#fde2e7', '#ffffff', '#f4b6c6'],
    leaf: ['#d99c4b', '#c05746', '#b8732f', '#e0a458', '#9c4a2f'],
    pollen: ['#ffe9a3', '#fff4c7', '#f5d77a'],
    firefly: ['#e9ff9a', '#fff6a8', '#c8ff8a'],
    snow: ['#ffffff'],
    frost: ['#dcecff', '#ffffff'],
};

function pickKind({ wx, isNight, season }) {
    if (wx.precip === 'hail' || wx.hail) return 'hail';
    if (wx.precip !== 'none') return wx.precip; // drizzle | rain | sleet | freezing | snow | grains
    if (wx.gale) return 'leaf'; // storm without rain: leaves and debris tear across the screen
    if (isNight) return season === 'winter' || wx.icy ? 'frost' : 'firefly';
    if (wx.icy) return 'frost';
    return { spring: 'petal', summer: 'pollen', autumn: 'leaf', winter: 'frost' }[season] || 'pollen';
}

const RAINY = { rain: 1, drizzle: 1, freezing: 1 };

function spawn(kind, w, h, anywhere) {
    // Sleet is a mix of rain and wet snow; hail storms carry rain too
    let t = kind;
    if (kind === 'sleet') t = Math.random() < 0.5 ? 'rain' : 'snow';
    if (kind === 'hail') t = Math.random() < 0.35 ? 'hail' : 'rain';
    if (kind === 'grains') t = 'snow';
    const pal = PALETTES[t] || PALETTES.pollen;
    const p = {
        t,
        x: Math.random() * w,
        y: anywhere ? Math.random() * h : -20,
        z: 0.4 + Math.random() * 0.6, // depth → size, speed, parallax
        rot: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 2,
        phase: Math.random() * Math.PI * 2,
        color: pal[(Math.random() * pal.length) | 0],
        icy: kind === 'freezing',
    };
    switch (t) {
        case 'rain':
            if (kind === 'drizzle') { p.vy = 380 + Math.random() * 120; p.size = 5 + Math.random() * 4; }
            else { p.vy = 700 + Math.random() * 300; p.size = 10 + Math.random() * 12; }
            break;
        case 'hail': p.vy = 900 + Math.random() * 300; p.size = 1.8 + Math.random() * 1.8; break;
        case 'snow': p.vy = (kind === 'grains' ? 90 : kind === 'sleet' ? 120 : 25) + Math.random() * 35; p.size = kind === 'grains' ? 1.2 + Math.random() : 1.5 + Math.random() * 2.5; break;
        case 'frost': p.vy = 8 + Math.random() * 10; p.size = 1 + Math.random() * 1.6; break;
        case 'petal': p.vy = 30 + Math.random() * 30; p.size = 4 + Math.random() * 4; break;
        case 'leaf': p.vy = 35 + Math.random() * 35; p.size = 5 + Math.random() * 5; break;
        case 'firefly':
            p.vy = (Math.random() - 0.5) * 12;
            p.size = 1.4 + Math.random() * 1.8;
            if (!anywhere) p.y = h * (0.3 + Math.random() * 0.7);
            break;
        default: p.vy = (Math.random() - 0.3) * 10; p.size = 1.2 + Math.random() * 2; if (!anywhere) p.y = Math.random() * h;
    }
    return p;
}

function draw(ctx, p, t, dark) {
    const kind = p.t;
    const s = p.size * p.z;
    ctx.save();
    ctx.translate(p.x, p.y);
    switch (kind) {
        case 'rain':
            ctx.strokeStyle = p.icy
                ? (dark ? 'rgba(200,235,255,0.55)' : 'rgba(60,120,170,0.4)')
                : (dark ? 'rgba(190,210,240,0.35)' : 'rgba(70,100,140,0.28)');
            ctx.lineWidth = p.z;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(-p.drift * 0.02, s);
            ctx.stroke();
            break;
        case 'petal':
        case 'leaf':
            ctx.rotate(p.rot);
            ctx.scale(1, 0.55 + 0.45 * Math.sin(t * 2 + p.phase)); // tumbling flip
            ctx.fillStyle = p.color;
            ctx.globalAlpha = kind === 'petal' ? 0.85 : 0.75;
            ctx.beginPath();
            ctx.ellipse(0, 0, s, s * (kind === 'leaf' ? 0.5 : 0.62), 0, 0, Math.PI * 2);
            ctx.fill();
            if (kind === 'leaf') {
                ctx.strokeStyle = 'rgba(0,0,0,0.18)';
                ctx.lineWidth = 0.6;
                ctx.beginPath();
                ctx.moveTo(-s, 0);
                ctx.lineTo(s, 0);
                ctx.stroke();
            }
            break;
        case 'hail':
            ctx.fillStyle = dark ? '#f4f8ff' : '#dfe8f5';
            ctx.strokeStyle = dark ? 'rgba(255,255,255,0.4)' : 'rgba(90,110,140,0.45)';
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.arc(0, 0, s + 0.8, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            break;
        case 'firefly': {
            const pulse = 0.35 + 0.65 * Math.max(0, Math.sin(t * 1.6 + p.phase));
            const g = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 6);
            g.addColorStop(0, p.color);
            g.addColorStop(1, 'rgba(233,255,154,0)');
            ctx.globalAlpha = pulse * (dark ? 0.9 : 0.5);
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(0, 0, s * 6, 0, Math.PI * 2);
            ctx.fill();
            break;
        }
        default: {
            // pollen / snow / frost: soft round motes that twinkle a little
            ctx.globalAlpha = (kind === 'snow' ? 0.9 : 0.55) * (0.6 + 0.4 * Math.sin(t * 2 + p.phase));
            if (!dark && kind !== 'pollen') ctx.globalAlpha *= 0.8;
            ctx.fillStyle = kind === 'snow' && !dark ? '#e8eef7' : p.color;
            ctx.beginPath();
            ctx.arc(0, 0, s, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    ctx.restore();
}

function ParticleField({ kind, intensity = 0.6, wind, calm }) {
    const ref = useRef(null);
    const windRef = useRef(wind);
    windRef.current = wind;

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;
        let w = 0;
        let h = 0;
        let particles = [];
        let raf = null;
        let last = performance.now();
        const dpr = Math.min(window.devicePixelRatio || 1, 1.25); // soft motes don't need retina sharpness

        const resize = () => {
            w = canvas.clientWidth;
            h = canvas.clientHeight;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const wet = kind in RAINY || kind === 'sleet' || kind === 'hail';
            const density = wet ? 9000 : kind === 'snow' || kind === 'grains' ? 12000 : kind === 'firefly' ? 30000 : 22000;
            const max = wet ? 140 : kind === 'snow' || kind === 'grains' ? 110 : 45;
            // Measured intensity drives how much falls (drizzle → cloudburst, flurries → blizzard)
            const strength = wet || kind === 'snow' || kind === 'grains' ? 0.25 + intensity * 0.9 : 1;
            const count = Math.min(max, Math.round(((w * h) / density) * strength)) * (calm ? 0.5 : 1);
            particles = Array.from({ length: Math.round(count) }, () => spawn(kind, w, h, true));
        };

        const frame = (now) => {
            if (document.hidden) { raf = null; return; }
            raf = requestAnimationFrame(frame);
            const dt = Math.min(0.05, (now - last) / 1000);
            if (dt < 1 / 30) return; // ~30 fps is plenty for ambience, easier on batteries
            // The page scrolling gets the whole frame budget; particles wait
            if (isScrolling()) { last = now; return; }
            last = now;
            const t = now / 1000;
            const dark = document.documentElement.classList.contains('dark');
            const gust = windRef.current * (1 + 0.5 * Math.sin(t * 0.4)); // wind breathes
            ctx.clearRect(0, 0, w, h);
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                const falling = p.t === 'rain' || p.t === 'hail';
                const sway = falling ? 0 : Math.sin(t * 0.9 + p.phase) * 18;
                p.drift = gust * (falling ? 260 : p.t === 'leaf' || p.t === 'snow' ? 140 : 60) + (p.t === 'firefly' ? Math.cos(t * 0.5 + p.phase) * 14 : 0);
                p.x += (p.drift + sway) * p.z * dt;
                p.y += (p.vy + (p.t === 'firefly' ? Math.sin(t * 0.7 + p.phase) * 10 : 0)) * p.z * dt;
                p.rot += p.spin * dt;
                // Hailstones bounce once off the bottom of the screen
                if (p.t === 'hail' && !p.bounced && p.y > h - 60 - p.z * 20) { p.bounced = true; p.vy = -p.vy * 0.25; }
                if (p.bounced) p.vy += 1600 * dt;
                if (p.y > h + 30 || p.y < -40 || p.x > w + 40 || p.x < -40) {
                    Object.assign(p, spawn(kind, w, h, kind === 'firefly' || kind === 'pollen' || kind === 'frost'));
                    // In a breeze, some particles blow in from the left edge
                    if (windRef.current > 0.3 && Math.random() < 0.35) { p.x = -20; p.y = Math.random() * h * 0.8; }
                }
                draw(ctx, p, t, dark);
            }
        };

        const onVis = () => {
            if (document.hidden) { cancelAnimationFrame(raf); raf = null; } else if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); }
        };

        resize();
        if (!document.hidden) raf = requestAnimationFrame(frame);
        window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [kind, calm, intensity]);

    return <canvas ref={ref} className="living-particles" />;
}

export default function LivingBackdrop() {
    const { pathname } = useLocation();
    // Eden owns its weather renderer; the camera must remain unobstructed.
    // Unmount instead of hiding so neither page pays for a second particle loop.
    if (pathname === '/eden' || pathname === '/id') return null;
    return <AmbientBackdrop />;
}

function AmbientBackdrop() {
    const living = useLiving();
    const ref = useRef(null);
    const { motion, isNight, season, wind, wx, sunX, sunY } = living;

    useEffect(() => motion === 'full' ? registerParallax(ref.current) : undefined, [motion]);

    // One distant, localized strike. Seasons change its cadence, not whether
    // thunder exists: that always comes from current reported weather.
    useEffect(() => {
        if (!wx.thunder || motion === 'off') return undefined;
        const element = ref.current;
        let strikeTimer, fadeTimer;
        const shoulderSeason = season === 'spring' || season === 'autumn';
        const firstDelay = motion === 'calm' ? 14000 : shoulderSeason ? 6000 : 9000;
        const nextDelay = () => motion === 'calm' ? 22000 + Math.random() * 12000
            : shoulderSeason ? 9000 + Math.random() * 8000 : 14000 + Math.random() * 12000;
        const clear = () => {
            clearTimeout(strikeTimer); clearTimeout(fadeTimer);
            element?.classList.remove('is-strike');
        };
        const strike = () => {
            if (document.hidden) return;
            element?.classList.add('is-strike');
            fadeTimer = setTimeout(() => element?.classList.remove('is-strike'), 700);
            strikeTimer = setTimeout(strike, nextDelay());
        };
        const visibility = () => {
            clear();
            if (!document.hidden) strikeTimer = setTimeout(strike, firstDelay);
        };
        visibility();
        document.addEventListener('visibilitychange', visibility);
        return () => { clear(); document.removeEventListener('visibilitychange', visibility); };
    }, [wx.thunder, motion, season]);

    const kind = pickKind({ wx, isNight, season });
    const cover = clamp((wx.cloud ?? 0) / 100);
    const cloudCount = Math.min(7, Math.max(wx.thunder || wx.precip !== 'none' ? 3 : 0, Math.round(cover * 7)));
    const clouds = useMemo(() => {
        const random = seeded('growdaily-ambient-clouds');
        return Array.from({ length: 7 }, (_, index) => ({
            left: 4 + index * 15 + random() * 6,
            top: 4 + random() * 26,
            width: 160 + random() * 160,
            speed: 0.8 + random() * 0.5,
            delay: -random() * 100,
        }));
    }, []);
    const darkClouds = wx.thunder || wx.precip !== 'none' || cover > 0.85;

    return (
        <div ref={ref} className="living-backdrop" aria-hidden data-testid="living-backdrop">
            <div className="living-sky" />
            <div className="living-sun" />
            <div className={`living-celestial ${isNight ? 'living-celestial--moon' : 'living-celestial--sun'}`}
                style={{ left: `${isNight ? clamp(100 - sunX, 12, 88) : sunX}%`, top: `${isNight ? 13 : clamp(sunY, 7, 34)}%`, opacity: 0.3 * (1 - cover * 0.7) }} />
            <div className={`living-clouds ${darkClouds ? 'living-clouds--dark' : ''}`}>
                {clouds.slice(0, cloudCount).map((cloud, index) => <span key={index} className="living-cloud"
                    style={{ left: `${cloud.left}%`, top: `${cloud.top}%`, width: `clamp(110px, ${cloud.width / 10}vw, ${cloud.width}px)`, opacity: 0.12 + cover * 0.18,
                        animationDuration: `${(150 - wind * 75) * cloud.speed}s`, animationDelay: `${cloud.delay}s` }}><CloudShape /></span>)}
            </div>
            <div className="living-aurora">
                <span className="living-blob b1" />
                <span className="living-blob b2" />
                <span className="living-blob b3" />
            </div>
            {wx.fog && <div className="living-fog" />}
            {wx.icy && <div className="living-frost" />}
            {motion !== 'off' && <ParticleField kind={kind} intensity={wx.intensity} wind={wind} calm={motion === 'calm'} />}
            {wx.thunder && motion !== 'off' && <div className="living-lightning">
                <span className="living-lightning__glow" />
                <svg viewBox="0 0 80 180" className="living-lightning__bolt" focusable="false">
                    <path d="M48 6L24 67H38L19 153L62 56H47L63 6Z" />
                    <path d="M34 82L60 96L49 125" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
            </div>}
        </div>
    );
}
